import React, { useState, useEffect } from 'react';
import { Send, Mail, RefreshCw, CheckCircle, AlertCircle, Inbox } from 'lucide-react';

interface EmailLog {
  id: string;
  timestamp: string;
  from: string;
  subject: string;
  text?: string;
  sessionId?: string;
  status: 'pending' | 'received' | 'processed';
}

interface WebhookData {
  from?: { email: string; name?: string };
  to?: Array<{ email: string; name?: string }>;
  subject?: string;
  text?: string;
  timestamp?: number;
}

export const EmailFeedbackTest: React.FC = () => {
  const [emailLogs, setEmailLogs] = useState<EmailLog[]>([]);
  const [isPolling, setIsPolling] = useState(false);
  const [testEmail, setTestEmail] = useState({
    to: 'claude@futr.bet',
    subject: `Test Session: assist-${Date.now()}`,
    body: 'This is a test email from the EmailFeedback component. Please reply with your feedback.'
  });
  const [lastWebhook, setLastWebhook] = useState<WebhookData | null>(null);

  // Poll for new webhook data
  useEffect(() => {
    if (!isPolling) return;

    const interval = setInterval(async () => {
      try {
        const apiUrl = window.location.hostname === 'localhost'
          ? '/api/email-feedback?limit=5'
          : 'https://futr-bet-api.b-9f2.workers.dev/api/email-feedback?limit=5';
        const response = await fetch(apiUrl);
        if (response.ok) {
          const data = await response.json();
          if (data.success && data.data && data.data.length > 0) {
            // Get the most recent email
            const mostRecent = data.data[0];
            setLastWebhook({
              from: { email: mostRecent.from_email },
              subject: mostRecent.subject,
              text: mostRecent.body_text || mostRecent.body_html,
              timestamp: new Date(mostRecent.received_at).getTime() / 1000
            });

            // Update email log status based on subject matching
            setEmailLogs(prev => prev.map(log => {
              const hasReply = data.data.some((email: any) =>
                email.subject?.includes(log.sessionId || '')
              );
              if (hasReply && log.status === 'pending') {
                return { ...log, status: 'received' as const };
              }
              return log;
            }));
          }
        }
      } catch (error) {
        console.error('Polling error:', error);
      }
    }, 5000);

    return () => clearInterval(interval);
  }, [isPolling]);

  const sendTestEmail = async () => {
    const sessionId = `assist-${Date.now()}`;
    const code = Math.random().toString(36).substr(2, 6).toUpperCase();

    // Log the outgoing email
    const newLog: EmailLog = {
      id: sessionId,
      timestamp: new Date().toISOString(),
      from: 'EmailFeedbackTest',
      subject: `Test Session: ${sessionId}`,
      text: testEmail.body,
      sessionId,
      status: 'pending'
    };

    setEmailLogs(prev => [newLog, ...prev]);
    setIsPolling(true);

    // Send via API with OTP code format
    try {
      // Use full API URL for CloudFlare Pages deployment
      const apiUrl = window.location.hostname === 'localhost'
        ? '/api/send-email'
        : 'https://futr-bet-api.b-9f2.workers.dev/api/send-email';

      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: testEmail.to,
          code: code
          // Now sends real emails via verified domain
        })
      });

      if (!response.ok) {
        let errorMessage = 'Failed to send email';
        try {
          const errorData = await response.json();
          errorMessage = errorData.error || errorMessage;
        } catch (e) {
          // If JSON parsing fails, use status text
          errorMessage = response.statusText || errorMessage;
        }
        throw new Error(errorMessage);
      }

      let data;
      try {
        data = await response.json();
      } catch (e) {
        // If response is not JSON, create a default response
        data = { success: true, message: 'Email sent (no JSON response)' };
      }
      console.log('Email sent successfully:', data);

      // Update the log with the actual sent message
      setEmailLogs(prev => prev.map(log =>
        log.id === sessionId
          ? {
              ...log,
              subject: `Your FutrBet code: ${code}`,
              text: `Code: ${code} - ${testEmail.body}`,
              status: 'processed' as const
            }
          : log
      ));

      // Show success message
      alert(`✅ Email sent successfully to ${testEmail.to}!\n\nMessage ID: ${data.messageId || 'sent'}`);

    } catch (error) {
      console.error('Error sending email:', error);
      alert(`Failed to send email: ${error instanceof Error ? error.message : 'Unknown error'}`);
      setEmailLogs(prev => prev.map(log =>
        log.id === sessionId
          ? { ...log, status: 'processed' as const }
          : log
      ));
    }
  };

  const clearLogs = () => {
    setEmailLogs([]);
    setLastWebhook(null);
    setIsPolling(false);
  };

  return (
    <div className="max-w-6xl mx-auto p-6 space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white p-6 rounded-lg shadow-lg">
        <h1 className="text-3xl font-bold mb-2">🧪 Email Feedback Test Console</h1>
        <p className="text-blue-100">
          Test the ImprovMX integration by sending emails and monitoring webhook responses
        </p>
      </div>

      {/* Email Composer */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
          <Send className="w-5 h-5" />
          Compose Test Email
        </h2>
        
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">To:</label>
            <input
              type="email"
              value={testEmail.to}
              onChange={(e) => setTestEmail({ ...testEmail, to: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="claude@futr.bet"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Subject:</label>
            <input
              type="text"
              value={testEmail.subject}
              onChange={(e) => setTestEmail({ ...testEmail, subject: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Test Session: assist-XXX"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Body:</label>
            <textarea
              value={testEmail.body}
              onChange={(e) => setTestEmail({ ...testEmail, body: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              rows={4}
              placeholder="Your test message..."
            />
          </div>
          
          <div className="flex gap-3">
            <button
              onClick={sendTestEmail}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors flex items-center gap-2"
            >
              <Send className="w-4 h-4" />
              Send Test Email
            </button>
            
            <button
              onClick={clearLogs}
              className="px-4 py-2 bg-gray-500 text-white rounded-md hover:bg-gray-600 transition-colors flex items-center gap-2"
            >
              <RefreshCw className="w-4 h-4" />
              Clear Logs
            </button>
          </div>
        </div>
      </div>

      {/* Status Display */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Email Log */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
            <Mail className="w-5 h-5" />
            Email Log
            {isPolling && (
              <span className="text-sm text-green-600 flex items-center gap-1">
                <RefreshCw className="w-3 h-3 animate-spin" />
                Polling...
              </span>
            )}
          </h2>
          
          <div className="space-y-3 max-h-96 overflow-y-auto">
            {emailLogs.length === 0 ? (
              <p className="text-gray-500 text-center py-8">No emails sent yet</p>
            ) : (
              emailLogs.map(log => (
                <div key={log.id} className="border rounded-lg p-3 space-y-2">
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <p className="font-medium text-sm">{log.subject}</p>
                      <p className="text-xs text-gray-600">Session: {log.sessionId}</p>
                      <p className="text-xs text-gray-500">{log.timestamp}</p>
                    </div>
                    <div>
                      {log.status === 'pending' && (
                        <span className="text-yellow-600 flex items-center gap-1 text-xs">
                          <AlertCircle className="w-3 h-3" />
                          Pending
                        </span>
                      )}
                      {log.status === 'received' && (
                        <span className="text-green-600 flex items-center gap-1 text-xs">
                          <CheckCircle className="w-3 h-3" />
                          Received
                        </span>
                      )}
                    </div>
                  </div>
                  {log.text && (
                    <p className="text-sm text-gray-700 border-t pt-2">{log.text}</p>
                  )}
                </div>
              ))
            )}
          </div>
        </div>

        {/* Webhook Response */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
            <Inbox className="w-5 h-5" />
            Last Webhook Response
          </h2>
          
          {lastWebhook ? (
            <div className="space-y-3">
              <div className="border rounded-lg p-3">
                <p className="text-sm font-medium mb-2">From:</p>
                <p className="text-sm text-gray-700">
                  {lastWebhook.from?.name || lastWebhook.from?.email || 'Unknown'}
                </p>
              </div>
              
              <div className="border rounded-lg p-3">
                <p className="text-sm font-medium mb-2">Subject:</p>
                <p className="text-sm text-gray-700">{lastWebhook.subject || 'No subject'}</p>
              </div>
              
              <div className="border rounded-lg p-3">
                <p className="text-sm font-medium mb-2">Message:</p>
                <p className="text-sm text-gray-700 whitespace-pre-wrap">
                  {lastWebhook.text || 'No text content'}
                </p>
              </div>
              
              {lastWebhook.timestamp && (
                <div className="border rounded-lg p-3">
                  <p className="text-sm font-medium mb-2">Timestamp:</p>
                  <p className="text-sm text-gray-700">
                    {new Date(lastWebhook.timestamp * 1000).toLocaleString()}
                  </p>
                </div>
              )}
              
              <details className="border rounded-lg p-3">
                <summary className="text-sm font-medium cursor-pointer">Raw JSON</summary>
                <pre className="text-xs mt-2 overflow-x-auto">
                  {JSON.stringify(lastWebhook, null, 2)}
                </pre>
              </details>
            </div>
          ) : (
            <p className="text-gray-500 text-center py-8">No webhook data received yet</p>
          )}
        </div>
      </div>

      {/* Instructions */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <h3 className="font-semibold text-blue-900 mb-2">📋 Testing Instructions:</h3>
        <ol className="list-decimal list-inside space-y-1 text-sm text-blue-800">
          <li>Configure your email forwarding service to POST to <code>https://futr.bet/api/email-feedback</code></li>
          <li>Click "Send Test Email" to send an email via Resend API</li>
          <li>Reply to the email from your email client to <code>claude@futr.bet</code></li>
          <li>Watch the webhook response appear in real-time (polls every 5 seconds)</li>
          <li>The system will automatically parse and display the feedback</li>
        </ol>
        <div className="mt-3 p-2 bg-yellow-50 border border-yellow-200 rounded">
          <p className="text-xs text-yellow-800">
            <strong>Note:</strong> Email sending requires RESEND_API_KEY configured in CloudFlare Worker.
            Webhook reception requires email forwarding service (ImprovMX, SendGrid, etc.) configured.
          </p>
        </div>
      </div>
    </div>
  );
};